import { useState, useEffect, useMemo } from 'react'
import { Box, LinearProgress, Alert, Fab, Button } from '@mui/material'
import * as Icons from '@mui/icons-material'
import _ from 'lodash'
import { useFarmId } from '~/common/use-farm-id'
import { useOrgParams } from '~/common/use-org-params'
import { useOrganizations } from '~/common/use-organizations'
import { setActiveOrgs, useOrgContextDispatch } from '~/common/context/organization/org-context'
import { OrgBreadcrumbs } from '~/dashboard/org-breadcrumbs'
import { TaskList } from './task-list'
import {
  APPBAR_HEIGHT_REM,
  APPBAR_EXTRA_DESKTOP_REM,
  CONTENT_PADDING_RBL_REM,
  CONTENT_PADDING_T_REM,
  DASHBOARD_TITLE_HEIGHT_REM,
  FULLSCREEN_HEIGHT_MD,
} from '~/common/constants/layout-constants'
import { TaskModal } from './add-task/task-modal'
import { useFeatures } from '~/common/use-features'
import { useIsSeasonReadOnly } from '~/farm/use-is-season-read-only'
import { IcsExportModal } from './components/ics-export-modal'
import { TasksContextProvider } from './context/tasks-context-provider'
import { TaskCalendar } from './calendar/task-calendar'

const TasksFullscreenInner = ({ farmId }) => {
  const dispatch = useOrgContextDispatch()
  const { plotId } = useOrgParams()
  const [addTaskOpen, setAddTaskOpen] = useState(false)
  const { features } = useFeatures()
  const seasonReadOnly = useIsSeasonReadOnly()

  const [icsModalOpen, setIcsModalOpen] = useState(false)

  const ids = useMemo(() => _.compact([farmId, plotId]), [farmId, plotId])
  const typeValidations = useMemo(
    () => (plotId ? ['farm', 'plot'] : ['farm']),
    [plotId]
  )

  const { data: organizations } = useOrganizations({ ids, typeValidations })

  const orderedOrgs = useMemo(() => {
    if (!organizations) return []
    return ids.map(id => organizations.find(org => org.id === id)).filter(Boolean)
  }, [organizations, ids])

  useEffect(() => {
    dispatch(setActiveOrgs({ activeOrgs: orderedOrgs }))
  }, [dispatch, orderedOrgs])

  return (
    <TasksContextProvider>
      <Box
        sx={{
          height: {
            xs: `calc(100vh - ${APPBAR_HEIGHT_REM} + ${APPBAR_EXTRA_DESKTOP_REM})`,
            md: `calc(100vh - ${APPBAR_HEIGHT_REM})`,
            lg: FULLSCREEN_HEIGHT_MD,
          },
          m: `-${CONTENT_PADDING_T_REM} -${CONTENT_PADDING_RBL_REM} -${CONTENT_PADDING_RBL_REM} -${CONTENT_PADDING_RBL_REM}`,
          pt: CONTENT_PADDING_T_REM,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Breadcrumbs header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: DASHBOARD_TITLE_HEIGHT_REM,
            mb: '0.25rem',
            pt: 1,
            flexShrink: 0,
            px: CONTENT_PADDING_RBL_REM,
          }}
        >
          <OrgBreadcrumbs baseRoute='tasks' />

          {features['export_ics'] && (
            <Button
              onClick={() => setIcsModalOpen(true)}
              size="small"
              endIcon={<Icons.CalendarMonth fontSize="small" />}
              variant="outlined"
            >
              Export ICS
            </Button>
          )}
        </Box>

        {/* Main content: task list (2fr) + calendar (1fr) */}
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            gap: 2,
            px: CONTENT_PADDING_RBL_REM,
            pb: CONTENT_PADDING_RBL_REM,
          }}
        >
          <Box sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <TaskList />
          </Box>
          <Box sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
            <TaskCalendar />
          </Box>
        </Box>

        {features['add_tasks'] && !seasonReadOnly && (
          <Fab
            size="small"
            color="primary"
            onClick={() => setAddTaskOpen(true)}
            sx={{
              position: 'absolute',
              bottom: 28,
              right: 16,
            }}
          >
            <Icons.Add />
          </Fab>
        )}
        <TaskModal open={addTaskOpen} onClose={() => setAddTaskOpen(false)} farmId={farmId} />
        <IcsExportModal open={icsModalOpen} onClose={() => setIcsModalOpen(false)} farmId={farmId} />
      </Box>
    </TasksContextProvider>
  )
}

const TasksFullscreen = () => {
  const { farmId, loading } = useFarmId()

  if (loading) {
    return <LinearProgress />
  }

  if (!farmId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Please select a farm to view the task list.</Alert>
      </Box>
    )
  }

  return <TasksFullscreenInner farmId={farmId} />
}

export default TasksFullscreen
